import type { ISpotlight, ICreateSpotlight } from 'src/types/story';

import { useState, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { Grid, Stack, Paper, Button, IconButton, Typography } from '@mui/material';

import { TabButton } from 'src/components/tab-button';

import { Iconify } from '../iconify';
import { RHFTextField } from './rhf-text-field';
import { SpotlightCanvas } from '../spotlights/spotlight-canvas';

// ----------------------------------------------------------------------

type Props = {
  name: string;
  helperText?: string;
  disabled?: boolean;
  imageFieldName?: string;
};

export function RHFSpotlights({ name, helperText, disabled, imageFieldName }: Props) {
  const { control, setValue, watch } = useFormContext();
  const spotlights = watch(name) || [];
  const [currentPage, setCurrentPage] = useState(0);
  const image = imageFieldName ? watch(imageFieldName) : null;
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  const onSpotlightChange = (index: number, rectangle: { x1: number; y1: number; x2: number; y2: number }) => {
    if (spotlights[index]) {
      const updatedSpotlights = [...spotlights];
      updatedSpotlights[index] = {
        ...updatedSpotlights[index],
        x1: rectangle.x1,
        y1: rectangle.y1,
        x2: rectangle.x2,
        y2: rectangle.y2,
      };
      setValue(name, updatedSpotlights, { shouldValidate: true });
    }
  };

  useEffect(() => {
    let url: string | null = null;
    
    if (image instanceof File) {
      url = URL.createObjectURL(image);
      setImageUrl(url);
    } else if (typeof image === 'string') {
      setImageUrl(image);
    } else {
      setImageUrl(null);
    }

    return () => {
      if (url) {
        URL.revokeObjectURL(url);
      }
    };
  }, [image]);

  // Watch for image changes and create first spotlight if needed
  useEffect(() => {
    if (image && spotlights.length === 0) {
      const newSpotlight: ICreateSpotlight = {
        name: 'Spotlight #1',
        index: 0,
        description: '',
        webURL: '',
        x1: 0.4,
        y1: 0.4,
        x2: 0.6,
        y2: 0.6,
        subtext: '',
      };
      setValue(name, [newSpotlight], { shouldValidate: true });
    }
  }, [image, spotlights.length, name, setValue]);

  const addSpotlight = () => {
    const newSpotlight: ICreateSpotlight = {
      name: `Spotlight #${spotlights.length + 1}`,
      index: spotlights.length,
      description: '',
      webURL: '',
      x1: 0.4,
      y1: 0.4,
      x2: 0.6,
      y2: 0.6,
      subtext: '',
    };
    setValue(name, [...spotlights, newSpotlight], { shouldValidate: true });
    setCurrentPage(spotlights.length); // Switch to the new spotlight
  };

  const removeSpotlight = (index: number) => {
    const newSpotlights = spotlights.filter((_: any, i: number) => i !== index);
    setValue(name, newSpotlights, { shouldValidate: true });
    // Adjust current page if needed
    if (currentPage >= newSpotlights.length) {
      setCurrentPage(Math.max(0, newSpotlights.length - 1));
    }
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <SpotlightCanvas
              imageUrl={imageUrl}
              spotlights={spotlights}
              currentPage={currentPage}
              disabled={disabled}
              onSpotlightChange={onSpotlightChange}
              onSpotlightSelect={setCurrentPage}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Stack spacing={2}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Stack 
                  direction="row" 
                  spacing={1} 
                  alignItems="center"
                  sx={{ flexWrap: 'wrap', gap: 1 }}
                >
                  <Button
                    startIcon={<Iconify icon="eva:plus-fill" />}
                    onClick={addSpotlight}
                    variant="contained"
                    disabled={disabled}
                  >
                    Add spotlight
                  </Button>
                  {spotlights.map((spotlight: ISpotlight, index: number) => (
                    <TabButton
                      key={index}
                      isSelected={currentPage === index}
                      onClick={() => setCurrentPage(index)}
                    >
                      {spotlight.name?.trim()
                        ? spotlight.name
                        : `Spotlight #${index + 1}`}
                    </TabButton>
                  ))}
                </Stack>
              </Stack>

              {spotlights.length > 0 && (
                <Paper
                  key={`fields-${currentPage}-${spotlights[currentPage]?.x1}-${spotlights[currentPage]?.y1}-${spotlights[currentPage]?.x2}-${spotlights[currentPage]?.y2}`}
                  elevation={1}
                  sx={{
                    p: 2,
                    position: 'relative',
                  }}
                >
                  <Stack spacing={2}>
                    <Stack spacing={1}>
                      <Typography variant="subtitle2">*Spotlight name</Typography>
                      <RHFTextField
                        name={`${name}.${currentPage}.name`}
                        fullWidth
                        disabled={disabled}
                      />
                    </Stack>

                    <Stack spacing={1}>
                      <Typography variant="subtitle2">Subtitle</Typography>
                      <RHFTextField
                        name={`${name}.${currentPage}.subtext`}
                        fullWidth
                        placeholder="Free text..."
                        disabled={disabled}
                      />
                    </Stack>

                    <Stack spacing={1}>
                      <Typography variant="subtitle2">Description</Typography>
                      <RHFTextField
                        name={`${name}.${currentPage}.description`}
                        multiline
                        rows={3}
                        fullWidth
                        placeholder="About this cool item..."
                        disabled={disabled}
                      />
                    </Stack>

                    <Stack spacing={1}>
                      <Typography variant="subtitle2">Learn more link</Typography>
                      <RHFTextField
                        name={`${name}.${currentPage}.webURL`}
                        fullWidth
                        disabled={disabled}
                        placeholder="https://"
                      />
                    </Stack>
                  </Stack>

                  <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
                    <IconButton
                      size="small"
                      onClick={() => removeSpotlight(currentPage)}
                      disabled={disabled || spotlights.length <= 1}
                      sx={{ color: 'error.main' }}
                    >
                      <Iconify icon="mdi:trash-can-outline" />
                    </IconButton>
                  </Stack>
                </Paper>
              )}
            </Stack>
          </Grid>
        </Grid>
      )}
    />
  );
}